import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart3, Calculator, ChevronDown, ChevronUp, ChevronsUpDown, Edit,
  FileSpreadsheet, FileText, Loader2, Mail, MessageCircle, Package, Printer, RefreshCw, X,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import type { AppBoleta, AppBoletaProducto, AppBoom } from '../../types/empacadora';
import { inputCls, labelCls, pageTitleCls, pageSubtitleCls } from '../../components/ui';
import { buildBoletaHTML, imprimirBoletas } from './boletaImprimir';
import { imprimirReingreso } from './reingresoImprimir';
import { buildApiUrl } from '../../utils/apiUrl';
import { ModalReporteCampo } from './ModalReporteCampo';
import {
  consolidarProductosSemana, buildProductosSemanaHTML, imprimirProductosSemana,
  exportarProductosSemanaCSV, type ConsolidadoSemana,
} from './productosSemanaImprimir';
import { recalcularBoleta } from './recalcularBoleta';
import { imprimirControlInventario } from './controlInventarioImprimir';
import { type BoletaControlCedulas, type FiltrosControlCedulas } from './controlCedulasImprimir';
import { ControlCedulasPreview } from './ControlCedulasPreview';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Formatea un TIMESTAMP completo (ej. created_at con hora/zona). A diferencia de
// fmtF, no le concatena 'T00:00:00' (eso rompería un ISO que ya trae hora).
function fmtFecha(s?: string | null) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// La fecha de creación solo es confiable/aplicable desde la semana 26-26 (formato "SS-AA").
// Devuelve un valor ordenable AAAASS; null si no se puede parsear.
function semanaOrden(semana?: string | null): number | null {
  const m = String(semana ?? '').trim().match(/^(\d{1,2})-(\d{2})$/);
  if (!m) return null;
  const sem = Number(m[1]); const anio = Number(m[2]);
  return anio * 100 + sem;   // 26-26 → 2626
}
const SEMANA_CREADA_DESDE = semanaOrden('26-26')!; // 2626
// Muestra la fecha de creación solo si la boleta es de la semana 26-26 en adelante.
function fmtCreada(created_at?: string | null, semana?: string | null) {
  const ord = semanaOrden(semana);
  if (ord == null || ord < SEMANA_CREADA_DESDE) return '—';
  return fmtFecha(created_at);
}
function fmtF(s?: string | null) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

// Rango legible: "1-31 may 2026" si mismo mes-año, "01 may - 15 jun 2026"
// si distinto mes mismo año, "01/05/2025 - 15/06/2026" si distintos años.
function fmtRangoCorto(desde: string, hasta: string): string {
  if (!desde || !hasta) return '';
  const d = new Date(desde + 'T00:00:00');
  const h = new Date(hasta + 'T00:00:00');
  if (Number.isNaN(d.getTime()) || Number.isNaN(h.getTime())) return '';
  const MESES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  const mismoAnio = d.getFullYear() === h.getFullYear();
  const mismoMes = mismoAnio && d.getMonth() === h.getMonth();
  if (mismoMes) {
    return `${d.getDate()}-${h.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (mismoAnio) {
    return `${String(d.getDate()).padStart(2,'0')} ${MESES[d.getMonth()]} - ${String(h.getDate()).padStart(2,'0')} ${MESES[h.getMonth()]} ${d.getFullYear()}`;
  }
  const f = (x: Date) => `${String(x.getDate()).padStart(2,'0')}/${String(x.getMonth()+1).padStart(2,'0')}/${x.getFullYear()}`;
  return `${f(d)} - ${f(h)}`;
}

function fmtN(n?: number | null, dec = 2) {
  if (n == null) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function pct(parte: number, total: number) {
  if (!total) return '0%';
  return `${fmtN((parte / total) * 100, 0)}%`;
}

function topValores(rows: AppBoleta[], key: keyof AppBoleta, take = 5) {
  const mapa = new Map<string, { label: string; total: number; plantas: number; area: number }>();
  for (const b of rows) {
    const label = String(b[key] ?? '').trim() || 'Sin dato';
    const curr = mapa.get(label) ?? { label, total: 0, plantas: 0, area: 0 };
    curr.total += 1;
    curr.plantas += Number((b as any).plantas ?? 0);
    curr.area += Number((b as any).area_ha ?? 0);
    mapa.set(label, curr);
  }
  return [...mapa.values()].sort((a, b) => b.total - a.total).slice(0, take);
}

type FiltroEstado = 'todos' | 'pendiente' | 'en_proceso' | 'aplicada' | 'impresa' | 'archivada' | 'cancelada' | 'manual' | 'vfp';

// Filtros alineados con el flujo real de la cédula (igual que Bodega/Boomero):
// Pendiente (generada) → En proceso (bodega/boomero) → Aplicada (cerrada) → Archivada
// ("Aplicar directo" desde oficina). El estado 'impresa' no forma parte del flujo,
// por eso no se ofrece como filtro (siempre daría 0).
const FILTROS: { key: FiltroEstado; label: string }[] = [
  { key: 'todos',      label: 'Todos' },
  { key: 'pendiente',  label: 'Pendientes' },
  { key: 'en_proceso', label: 'En proceso' },
  { key: 'aplicada',   label: 'Aplicados' },
  { key: 'archivada',  label: 'Archivadas' },
  { key: 'manual',     label: 'Manuales' },
  { key: 'vfp',        label: 'Histórico VFP' },
  { key: 'cancelada',  label: 'Cancelados' },
];

const ESTADO_CFG: Record<string, { label: string; bg: string; border: string; text: string }> = {
  pendiente:  { label: 'Pendiente',  bg: 'var(--tone-warn-bg)',    border: 'var(--tone-warn-border)',    text: 'var(--tone-warn-text)' },
  en_proceso: { label: 'En proceso', bg: 'var(--tone-info-bg)',    border: 'var(--tone-info-border)',    text: 'var(--tone-info-text)' },
  impresa:    { label: 'Impresa',    bg: 'var(--tone-info-bg)',    border: 'var(--tone-info-border)',    text: 'var(--tone-info-text)' },
  aplicada:   { label: 'Aplicado',   bg: 'var(--tone-success-bg)', border: 'var(--tone-success-border)', text: 'var(--tone-success-text)' },
  archivada:  { label: 'Archivada',  bg: 'var(--tone-success-bg)', border: 'var(--tone-success-border)', text: 'var(--tone-success-text)' },
  cancelada:  { label: 'Cancelada',  bg: 'var(--surface-deep)',    border: 'var(--line)',                text: 'var(--ink-faint)' },
};

// ─── Columnas ordenables ──────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';
type SortKey = keyof AppBoleta;

interface ColDef {
  key: SortKey;
  label: string;
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
}

const COLS: (ColDef | { key: null; label: string; align?: 'left' | 'right' | 'center' })[] = [
  { key: null,              label: '',          align: 'center' }, // chevron acordeón
  { key: null,              label: 'Activo'    },
  { key: 'numero',          label: 'Boleta'    },
  { key: 'semana',          label: 'Semana'    },
  { key: 'created_at',      label: 'Creada'    },
  { key: 'estado',          label: 'Estado'    },
  { key: null,              label: 'Imprimir'  },
  { key: 'fecha_aplicacion',label: 'F. Aplicado' },
  { key: 'fecha_plan',      label: 'Aplicar el', align: 'left' },
  { key: 'boom_capacidad_l',label: 'Boom',     align: 'right', numeric: true },
  { key: 'agua_l',          label: 'Agua',     align: 'right', numeric: true },
  { key: 'lote_num',        label: 'Lote'      },
  { key: 'plantas',         label: 'Plantas',  align: 'right', numeric: true },
  { key: 'area_ha',         label: 'Área',     align: 'right', numeric: true },
  { key: 'etapa_nombre',    label: 'Plan'      },
  { key: 'ciclo_nombre',    label: 'Ciclo'     },
  { key: 'dias',            label: 'Días',     align: 'right', numeric: true },
  { key: 'fecha_siembra',   label: 'Siembra'   },
  { key: 'bloques',         label: 'Bloques'   },
];

function sortBoletas(arr: AppBoleta[], key: SortKey, dir: SortDir): AppBoleta[] {
  return [...arr].sort((a, b) => {
    const va = a[key] ?? '';
    const vb = b[key] ?? '';
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey | null; sortKey: SortKey | null; sortDir: SortDir }) {
  if (col === null) return null;
  if (col !== sortKey) return <ChevronsUpDown size={10} style={{ opacity: 0.35 }} />;
  return sortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />;
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function CedulasAplicacionPage() {
  const empresaId = useEmpresaId();
  const permisos = useAppPermisos();

  // Filtros
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  // El filtro de fechas NO se aplica hasta que el usuario lo active (botón "Filtrar por fecha").
  // Por defecto muestra todo el historial sin recortar por rango.
  const [filtroFechasActivo, setFiltroFechasActivo] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaSemana, setBusquedaSemana] = useState('');
  const [semanasDisponibles, setSemanasDisponibles] = useState<string[]>([]);
  const [selectorSemanaAbierto, setSelectorSemanaAbierto] = useState(false);
  const selectorSemanaRef = useRef<HTMLDivElement | null>(null);
  // Último número cargado (para mostrar como placeholder de referencia)
  const [ultimoNumero, setUltimoNumero] = useState('');
  // Rango de fechas previo al entrar a "Histórico VFP" (para restaurarlo al salir)
  const rangoPrevioRef = useRef<{ desde: string; hasta: string } | null>(null);

  // Datos
  const [boletas, setBoletas]   = useState<AppBoleta[]>([]);
  const [productos, setProductos] = useState<AppBoletaProducto[]>([]);
  const [boletaSel, setBoletaSel] = useState<AppBoleta | null>(null);
  // Conteos por estado (en el rango/búsqueda activos, sin filtro de estado)
  const [conteos, setConteos] = useState<Record<string, number>>({});
  const [refreshTrigger] = useState(0);
  // Acordeón: id de la boleta expandida (solo una a la vez)
  const [expandidoId, setExpandidoId] = useState<number | null>(null);
  // Cache de productos por boleta para no recargar al abrir/cerrar
  const [productosCache, setProductosCache] = useState<Record<number, AppBoletaProducto[]>>({});
  const [loadingExpandido, setLoadingExpandido] = useState<number | null>(null);
  // Movimiento de inventario por producto de boleta: set de boleta_producto_id
  // que SÍ tienen movimiento, cargado en una sola consulta por boleta expandida
  // (no una consulta por fila — así no se vuelve pesado).
  const [movimientosBoleta, setMovimientosBoleta] = useState<Record<number, Set<number>>>({});
  const [movModal, setMovModal] = useState<{ boletaId: number; prodId: number; nombre: string; boletaNumero: string } | null>(null);
  const [movModalData, setMovModalData] = useState<any[] | null>(null);
  const [movModalLoading, setMovModalLoading] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [enviandoWsp, setEnviandoWsp] = useState(false);
  const [wspMsg, setWspMsg] = useState('');
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [empresaCedula, setEmpresaCedula] = useState('');
  const [empresaLogo, setEmpresaLogo]     = useState('');
  // Modal de edición del reporte de campo
  const [editarReporte, setEditarReporte] = useState<AppBoleta | null>(null);
  const [boomsList, setBoomsList] = useState<AppBoom[]>([]);
  const [usuarioActual, setUsuarioActual] = useState('');
  const [modalEstadistica, setModalEstadistica] = useState(false);
  // Modal reporte "Productos requeridos por semana" (consolidado)
  const [modalProductos, setModalProductos] = useState(false);
  const [consolidado, setConsolidado] = useState<ConsolidadoSemana | null>(null);
  const [cargandoConsolidado, setCargandoConsolidado] = useState(false);
  const [enviandoProdEmail, setEnviandoProdEmail] = useState(false);
  const [enviandoProdWsp, setEnviandoProdWsp] = useState(false);
  const [prodMsg, setProdMsg] = useState('');

  useEffect(() => {
    if (!selectorSemanaAbierto) return;
    const cerrarFuera = (event: MouseEvent) => {
      if (!selectorSemanaRef.current?.contains(event.target as Node)) {
        setSelectorSemanaAbierto(false);
      }
    };
    document.addEventListener('mousedown', cerrarFuera);
    return () => document.removeEventListener('mousedown', cerrarFuera);
  }, [selectorSemanaAbierto]);
  // Acordeón del reporte: clave (articulo||medida) del producto expandido
  const [prodExpandido, setProdExpandido] = useState<string | null>(null);
  // Recalcular cédula (área/plantas corregidos en catálogo → recalcula y vuelve a bodega)
  const [recalcularCedula, setRecalcularCedula] = useState<AppBoleta | null>(null);
  const [recalculando, setRecalculando] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState('');
  // Reporte Control de Inventario (requerido vs saldo)
  const [generandoInventario, setGenerandoInventario] = useState(false);
  const [generandoControlAb, setGenerandoControlAb] = useState(false);
  const [previewControlCedulas, setPreviewControlCedulas] = useState<{ boletas: BoletaControlCedulas[]; filtros: FiltrosControlCedulas } | null>(null);
  const [controlAbMsg, setControlAbMsg] = useState('');

  // Ordenamiento
  const [sortKey, setSortKey]   = useState<SortKey | null>('numero');
  const [sortDir, setSortDir]   = useState<SortDir>('desc');

  // Marcadas para imprimir
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());

  const tablaRef = useRef<HTMLDivElement>(null);
  const rowRefs  = useRef<(HTMLTableRowElement | null)[]>([]);
  const cargarSeqRef = useRef(0);

  function toggleMarcada(id: number) {
    setMarcadas(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Marcar / desmarcar TODAS las boletas actualmente visibles (filtradas).
  function toggleTodas(ids: number[]) {
    setMarcadas(prev => {
      const todasMarcadas = ids.length > 0 && ids.every(id => prev.has(id));
      const next = new Set(prev);
      if (todasMarcadas) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }

  // Totales header
  const totalPlantas = boletas.reduce((s, b) => s + (b.plantas ?? 0), 0);
  const totalArea    = boletas.reduce((s, b) => s + Number(b.area_ha ?? 0), 0);
  const densMedia    = totalArea > 0 ? totalPlantas / totalArea : 0;
  const aguaTotal    = boletas.reduce((s, b) => s + Number(b.agua_total_l ?? 0), 0);
  const booms        = boletas.reduce((s, b) => s + (b.n_booms_completos ?? 0), 0);

  // Cargar al montar con el mes actual como rango inicial + datos empresa
  // Cambio de filtro. "Histórico VFP" cubre 2025+ (el rango por defecto es el mes
  // actual y dejaría las boletas viejas fuera); al salir, restaura el rango previo.
  const cambiarFiltro = (key: FiltroEstado) => {
    if (key === 'vfp' && filtroEstado !== 'vfp') {
      rangoPrevioRef.current = { desde: fechaDesde, hasta: fechaHasta };
      setFechaDesde('2025-01-01');
      setFechaHasta(new Date().toISOString().slice(0, 10));
    } else if (key !== 'vfp' && filtroEstado === 'vfp' && rangoPrevioRef.current) {
      setFechaDesde(rangoPrevioRef.current.desde);
      setFechaHasta(rangoPrevioRef.current.hasta);
      rangoPrevioRef.current = null;
    }
    setFiltroEstado(key);
  };

  useEffect(() => {
    // Prellenar el rango con el mes actual (solo como SUGERENCIA en los inputs);
    // NO se activa el filtro: el historial arranca mostrando todo hasta que el
    // usuario pulse "Filtrar por fecha".
    const hoy = new Date();
    const ini = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
    const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10);
    setFechaDesde(ini);
    setFechaHasta(fin);
    Promise.all([
      supabase.from('empresas').select('nombre, cedula').eq('id', empresaId).single(),
      supabase.from('fe_config_empresa').select('logo_url').eq('empresa_id', empresaId).maybeSingle(),
    ]).then(([{ data: emp }, { data: cfg }]) => {
      if (emp) {
        setEmpresaNombre((emp as any).nombre ?? '');
        setEmpresaCedula((emp as any).cedula ?? '');
      }
      setEmpresaLogo((cfg as any)?.logo_url ?? '');
    });
    // Booms para el modal de edición de reporte
    supabase
      .from('app_booms')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('activo', true)
      .order('nombre')
      .then(({ data }) => setBoomsList((data ?? []) as AppBoom[]));
    // Usuario actual
    supabase.auth.getUser().then(({ data }) => {
      setUsuarioActual((data.user?.user_metadata?.nombre as string) || data.user?.email || 'usuario');
    });
    // Última boleta cargada → placeholder de referencia en el buscador
    supabase
      .from('app_boletas')
      .select('numero, semana')
      .eq('empresa_id', empresaId)
      .neq('estado', 'borrador')
      .order('numero', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setUltimoNumero((data as any).numero ?? '');
        }
      });

    // Historial completo de semanas (ERP + histórico VFP). La vista inicia sin
    // filtro; el usuario elige la semana cuando la necesita.
    const cargarSemanas = async () => {
      const leerSemanas = async (tabla: 'app_boletas' | 'app_vfp_cedula') => {
        const resultado: string[] = [];
        const pagina = 1000;
        for (let desde = 0; ; desde += pagina) {
          let consulta = supabase
            .from(tabla)
            .select('semana')
            .eq('empresa_id', empresaId)
            .range(desde, desde + pagina - 1);
          if (tabla === 'app_boletas') consulta = consulta.neq('estado', 'borrador');
          const { data, error } = await consulta;
          if (error || !data) break;
          resultado.push(...data.map((r: any) => String(r.semana ?? '').trim()).filter(Boolean));
          if (data.length < pagina) break;
        }
        return resultado;
      };

      const [erp, vfp] = await Promise.all([
        leerSemanas('app_boletas'),
        leerSemanas('app_vfp_cedula'),
      ]);
      const semanas = [...new Set([...erp, ...vfp])].sort((a, b) => {
        const oa = semanaOrden(a) ?? -1;
        const ob = semanaOrden(b) ?? -1;
        return ob - oa || b.localeCompare(a, 'es', { numeric: true });
      });
      setSemanasDisponibles(semanas);
    };
    cargarSemanas();
  }, [empresaId]);

  const fetchProductosBoleta = async (boletaId: number) => {
    const { data } = await supabase
      .from('app_boleta_productos').select('*')
      .eq('boleta_id', boletaId).order('es_agua').order('orden');
    return (data ?? []) as AppBoletaProducto[];
  };

  // Trae el reporte del boomero (si existe) para imprimirlo en la cédula
  const fetchReporteCampo = async (boletaId: number) => {
    const { data } = await supabase
      .from('app_boleta_campo')
      .select('*')
      .eq('boleta_id', boletaId)
      .maybeSingle();
    return data as any;
  };

  const marcadasImprimibles = useMemo(
    () => boletas.filter(b => marcadas.has(b.id) && !b.manual),
    [boletas, marcadas],
  );

  const handleImprimirSeleccionada = async () => {
    if (!boletaSel) return;
    setImprimiendo(true);
    try {
      await imprimirBoletas([boletaSel], fetchProductosBoleta, empresaNombre, empresaCedula, empresaLogo, fetchReporteCampo);
    } finally {
      setImprimiendo(false);
    }
  };

  const handleImprimirMarcadas = async () => {
    const lista = marcadasImprimibles;
    if (lista.length === 0) return;
    setImprimiendo(true);
    try {
      await imprimirBoletas(lista, fetchProductosBoleta, empresaNombre, empresaCedula, empresaLogo, fetchReporteCampo);
    } finally {
      setImprimiendo(false);
    }
  };

  const handleImprimirRE08 = async () => {
    const lista = marcadas.size > 0
      ? marcadasImprimibles
      : boletas.filter(b => !b.manual);
    if (lista.length === 0) return;
    setImprimiendo(true);
    try {
      await imprimirReingreso(lista, fetchProductosBoleta, empresaNombre, empresaCedula, empresaLogo);
    } finally {
      setImprimiendo(false);
    }
  };

  // Mismos filtros de siempre para "Control cédulas" — ahora alimentan la
  // vista previa en pantalla (imprimir/enviar desde ahí), en vez de ir
  // directo a un diálogo de impresión oculto.
  function datosControlCedulasActual(): { boletas: BoletaControlCedulas[]; filtros: FiltrosControlCedulas } {
    const semanaEnProceso = semanasDisponibles[0];
    const cedulasPendientesOficina = boletasOrdenadas.filter(b =>
      b.semana !== semanaEnProceso
      && b.estado !== 'archivada'
      && b.estado !== 'cancelada'
    );
    return {
      boletas: cedulasPendientesOficina,
      filtros: {
        estado: filtroEstado,
        semana: busquedaSemana.trim() || undefined,
        semanaEnProceso,
        fechaDesde,
        fechaHasta,
        filtroFechasActivo,
        boleta: busqueda.trim() || undefined,
      },
    };
  }

  const handleControlCedulas = () => {
    setPreviewControlCedulas(datosControlCedulasActual());
  };

  const handleSeguimientoActivoBiologico = async () => {
    setGenerandoControlAb(true);
    setControlAbMsg('');
    try {
      const { data: ultimoCorte, error: errorCorte } = await supabase
        .from('app_ab_preasientos')
        .select('periodo_hasta')
        .eq('empresa_id', empresaId)
        .in('estado', ['contabilizado', 'reversado'])
        .order('periodo_hasta', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (errorCorte) throw errorCorte;

      const corteContable = String(ultimoCorte?.periodo_hasta || '');
      if (!corteContable) {
        throw new Error('No existe un corte firme de Activo Biológico para iniciar el seguimiento.');
      }

      const [
        { data: cedulas, error: errorCedulas },
        { data: movimientos, error: errorMovimientos },
        { data: costosCongelados, error: errorCostos },
      ] = await Promise.all([
        supabase
          .from('app_boletas')
          .select('*')
          .eq('empresa_id', empresaId)
          .in('estado', ['aplicada', 'archivada'])
          .gt('fecha_aplicacion', corteContable)
          .order('fecha_aplicacion', { ascending: true })
          .order('numero', { ascending: true }),
        supabase
          .from('inv_movimientos')
          .select('id,fecha,referencia,cantidad,costo_unitario')
          .eq('empresa_id', empresaId)
          .eq('tipo', 'salida')
          .gt('fecha', corteContable)
          .like('referencia', 'APP-BOLETA-%'),
        supabase
          .from('app_ab_costos_congelados')
          .select('inv_movimiento_id')
          .eq('empresa_id', empresaId),
      ]);
      if (errorCedulas) throw errorCedulas;
      if (errorMovimientos) throw errorMovimientos;
      if (errorCostos) throw errorCostos;

      const movimientosPorCedula = new Map<number, any[]>();
      for (const movimiento of movimientos || []) {
        const match = String(movimiento.referencia || '')
          .match(/^APP-BOLETA-(?:DIRECTA-)?(\d+)-PROD-/i);
        const boletaId = Number(match?.[1] || 0);
        if (!boletaId) continue;
        movimientosPorCedula.set(boletaId, [
          ...(movimientosPorCedula.get(boletaId) || []),
          movimiento,
        ]);
      }
      const movimientosCongelados = new Set(
        (costosCongelados || []).map(row => String(row.inv_movimiento_id)),
      );

      const seguimiento = ((cedulas || []) as AppBoleta[]).map((cedula): BoletaControlCedulas => {
        const relacionados = movimientosPorCedula.get(Number(cedula.id)) || [];
        let controlAb = 'SIN MOVIMIENTO';
        if (relacionados.length > 0) {
          const todosCongelados = relacionados.every(mov =>
            movimientosCongelados.has(String(mov.id)),
          );
          const todosValorizados = relacionados.every(mov =>
            Number(mov.cantidad || 0) > 0 && Number(mov.costo_unitario || 0) > 0,
          );
          controlAb = todosCongelados
            ? 'INCLUIDA EN CORTE'
            : todosValorizados
              ? 'LISTA PARA PREPARAR'
              : 'COSTO CERO';
        }
        return {
          ...cedula,
          control_ab: controlAb,
          movimientos_ab: relacionados.length,
        };
      });

      imprimirControlCedulas(
        seguimiento,
        {
          estado: 'aplicada',
          tipo: 'activo-biologico',
          corteContable,
          filtroFechasActivo: false,
        },
        empresaNombre,
        empresaCedula,
        empresaLogo,
      );
      setControlAbMsg(
        `Seguimiento generado: ${seguimiento.length} cédulas posteriores al corte ${corteContable}.`,
      );
    } catch (error: any) {
      setControlAbMsg(error?.message || 'No se pudo generar el seguimiento de Activo Biológico.');
    } finally {
      setGenerandoControlAb(false);
    }
  };

  // Reporte "Control de Inventario": requerido (Σ cantidad_aplicar) vs saldo
  // (inv_productos), con faltantes. Opera sobre las cédulas visibles (mismo filtro
  // que el reporte de productos: ERP, no manuales, no canceladas).
  const handleControlInventario = async () => {
    if (boletas.length === 0) return;
    setGenerandoInventario(true);
    try {
      const res = await imprimirControlInventario(
        boletas, empresaId,
        {
          semana: busquedaSemana.trim() || undefined,
          fechaDesde: filtroFechasActivo ? fechaDesde : undefined,
          fechaHasta: filtroFechasActivo ? fechaHasta : undefined,
        },
        empresaNombre, empresaCedula, empresaLogo,
      );
      if (!res.ok) setEmailMsg(res.error || 'No se pudo generar el reporte de inventario.');
    } finally {
      setGenerandoInventario(false);
    }
  };

  // ── Reporte consolidado "Productos requeridos por semana" ──────────────────
  // Abre el modal y carga los productos de TODAS las boletas visibles (excepto
  // canceladas), en una sola query batch, para sumar por producto.
  const abrirReporteProductos = async () => {
    setModalProductos(true);
    setProdMsg('');
    setProdExpandido(null);
    setCargandoConsolidado(true);
    setConsolidado(null);
    try {
      // Solo boletas ERP (id > 0) no canceladas — VFP no tiene detalle sumable aquí.
      // Las MANUALES se excluyen: su producto ya se descontó del inventario (boleta
      // física), así que no forman parte del "requerido" para bodega/compras.
      const incluidas = boletas.filter(b => b.id > 0 && b.estado !== 'cancelada' && !b.manual);
      const ids = incluidas.map(b => b.id);
      const mapa = new Map<number, AppBoletaProducto[]>();
      // Batches de 200 ids para no exceder el límite de la URL de PostgREST.
      for (let i = 0; i < ids.length; i += 200) {
        const slice = ids.slice(i, i + 200);
        const { data } = await supabase
          .from('app_boleta_productos')
          .select('*')
          .in('boleta_id', slice);
        for (const p of (data ?? []) as AppBoletaProducto[]) {
          const arr = mapa.get(p.boleta_id) ?? [];
          arr.push(p);
          mapa.set(p.boleta_id, arr);
        }
      }
      // El reporte refleja el filtro ACTIVO de la pantalla: la semana del buscador,
      // o el rango de fechas si está activo. El consolidado suma solo lo visible.
      setConsolidado(consolidarProductosSemana(incluidas, mapa, {
        semana: busquedaSemana.trim() || undefined,
        fechaDesde: filtroFechasActivo ? fechaDesde : undefined,
        fechaHasta: filtroFechasActivo ? fechaHasta : undefined,
      }));
    } catch {
      setProdMsg('No se pudieron cargar los productos.');
    } finally {
      setCargandoConsolidado(false);
    }
  };

  const handleImprimirProductos = () => {
    if (!consolidado) return;
    imprimirProductosSemana(consolidado, empresaNombre, empresaCedula, empresaLogo);
  };

  // Confirma y ejecuta el recálculo de la cédula con el área/plantas del catálogo.
  const confirmarRecalculo = async () => {
    if (!recalcularCedula) return;
    setRecalculando(true);
    setRecalcMsg('');
    const res = await recalcularBoleta(recalcularCedula, empresaId);
    setRecalculando(false);
    if (!res.ok) {
      setRecalcMsg(res.error || 'No se pudo recalcular.');
      return;
    }
    setRecalcMsg(
      `Recalculada: área ${fmtN(res.areaAnterior)} → ${fmtN(res.areaNueva)} ha, ` +
      `plantas ${(res.plantasAnterior ?? 0).toLocaleString('es-CR')} → ${(res.plantasNueva ?? 0).toLocaleString('es-CR')}. ` +
      `Estado: Pendiente (lista para bodega).`,
    );
    // Refrescar la tabla y cerrar el diálogo tras un momento para que se lea el mensaje.
    await cargar();
    setTimeout(() => { setRecalcularCedula(null); setRecalcMsg(''); }, 2600);
  };

  const enviarReporteProductos = async (canal: 'email' | 'whatsapp') => {
    if (!consolidado) return;
    const setEnv = canal === 'email' ? setEnviandoProdEmail : setEnviandoProdWsp;
    setEnv(true);
    setProdMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const reporteHtml = buildProductosSemanaHTML(consolidado, empresaNombre, empresaCedula, empresaLogo);
      const resp = await fetch(buildApiUrl(`/aplicaciones/reportes/productos-semana/${canal}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          empresa_nombre: empresaNombre,
          titulo: consolidado.titulo,
          reporte_html: reporteHtml,
          resumen: {
            boletas: consolidado.boletasIncluidas,
            productos: consolidado.productos.length,
            area: consolidado.areaTotal,
            plantas: consolidado.plantasTotal,
          },
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || payload?.message || 'No se pudo enviar el reporte');
      setProdMsg(payload?.mensaje || 'Enviado correctamente.');
    } catch (error: any) {
      setProdMsg(error?.message || 'No se pudo enviar el reporte.');
    } finally {
      setEnv(false);
    }
  };

  const handleEnviarWspAplicacion = async () => {
    if (!boletaSel) return;
    if (boletaSel.estado !== 'aplicada') {
      setWspMsg('Seleccione una boleta aplicada para enviar la notificacion.');
      return;
    }
    setEnviandoWsp(true);
    setWspMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const ubicacion = boletaSel.finca_nombre ?? '';
      const productosEmail = productos.length > 0 ? productos : await fetchProductosBoleta(boletaSel.id);
      const boletaHtml = buildBoletaHTML(boletaSel, productosEmail, empresaNombre, empresaCedula, ubicacion, empresaLogo);
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/whatsapp-aplicacion`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ empresa_id: empresaId, boleta_html: boletaHtml }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.error || 'No se pudo enviar el WhatsApp');
      }
      setWspMsg(payload?.mensaje || 'WhatsApp enviado correctamente.');
    } catch (error: any) {
      setWspMsg(error?.message || 'No se pudo enviar el WhatsApp.');
    } finally {
      setEnviandoWsp(false);
    }
  };

  const handleEnviarEmailAplicacion = async () => {
    if (!boletaSel) return;
    if (boletaSel.estado !== 'aplicada') {
      setEmailMsg('Seleccione una boleta aplicada para enviar la notificacion.');
      return;
    }

    setEnviandoEmail(true);
    setEmailMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const ubicacion = boletaSel.finca_nombre ?? '';
      const productosEmail = productos.length > 0 ? productos : await fetchProductosBoleta(boletaSel.id);
      const boletaHtml = buildBoletaHTML(boletaSel, productosEmail, empresaNombre, empresaCedula, ubicacion, empresaLogo);
      const resp = await fetch(buildApiUrl(`/aplicaciones/boletas/${boletaSel.id}/email-aplicacion`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ empresa_id: empresaId, boleta_html: boletaHtml }),
      });

      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.message || payload?.error || 'No se pudo enviar el correo');
      }

      setEmailMsg(payload?.mensaje || 'Correo enviado correctamente.');
    } catch (error: any) {
      setEmailMsg(error?.message || 'No se pudo enviar el correo.');
    } finally {
      setEnviandoEmail(false);
    }
  };

  // Nota: el sync VFP se movió a la sección Configuración → Sync VFP.

  const cargar = useCallback(async () => {
    const seq = cargarSeqRef.current + 1;
    cargarSeqRef.current = seq;
    setLoading(true);

    // Búsqueda LIBRE por número: si hay texto en `busqueda`, se busca SOLO por número
    // en todo el historial, ignorando el filtro de fechas y el de estado.
    const buscandoNumero = !!busqueda.trim();
    // El filtro de fechas solo aplica si el usuario lo activó Y no está buscando por número.
    const aplicarFechas = filtroFechasActivo && fechaDesde && fechaHasta && !buscandoNumero;

    // Modo HISTÓRICO VFP: consultar app_vfp_cedula y mapear a shape AppBoleta
    if (filtroEstado === 'vfp') {
      let qv = supabase
        .from('app_vfp_cedula')
        .select('*')
        .eq('empresa_id', empresaId)
        .order('nboleta', { ascending: false });
      if (aplicarFechas) qv = qv.gte('faplica', fechaDesde).lte('faplica', fechaHasta);
      if (busqueda.trim()) qv = qv.ilike('nboleta', `%${busqueda.trim()}%`);
      if (!buscandoNumero && busquedaSemana.trim()) qv = qv.ilike('semana', `%${busquedaSemana.trim()}%`);
      const { data: dataVfp, error: errVfp } = await qv;
      if (seq !== cargarSeqRef.current) return;
      setLoading(false);
      if (!errVfp) {
        const mapeadas = (dataVfp ?? []).map((v: any): AppBoleta => ({
          id: -Number(v.nboleta),  // ID negativo para distinguir de las nuevas
          empresa_id: v.empresa_id,
          numero: v.nboleta,
          semana: v.semana ?? '',
          // Normalizar lote_num VFP (1 dígito) al formato ERP (2 dígitos con padding).
          lote_num: (() => {
            const r = String(v.lote_num ?? '').trim();
            return /^\d$/.test(r) ? '0' + r : r;
          })(),
          finca_nombre: v.finca ?? '',
          bloques: v.bloques ?? '',
          plantas: v.plantas ?? 0,
          area_ha: v.area_ha ?? 0,
          dias: v.dias ?? 0,
          fecha_siembra: v.fsiembra ?? '',
          fecha_plan: v.faplica ?? '',
          fecha_aplicacion: v.fechaaplic ?? '',
          fecha_desde: v.faplica ?? '',
          fecha_hasta: v.faplica ?? '',
          etapa_nombre: v.nestadio ?? '',
          ciclo_nombre: v.ciclo ?? '',
          estado: v.aplicado ? 'archivada' : 'cancelada',
          aplicado_por: v.usuarioapl ?? '',
          aplicado_at: v.fechaaplic ?? '',
          despachado_por: v.usuario ?? '',
          agua_l: v.agua_l ?? 0,
          boom_capacidad_l: v.boom_l ?? 0,
          n_booms_completos: v.cboom ?? 0,
          litros_fraccion: v.cfraccion ?? 0,
          manual: !!v.manual,
        } as any));
        setBoletas(mapeadas);
        setBoletaSel(prev => prev ? mapeadas.find(b => b.numero === prev.numero) ?? null : null);
        setConteos({ todos: 0, vfp: mapeadas.length });
      }
      return;
    }

    let q = supabase
      .from('app_boletas')
      .select('*')
      .eq('empresa_id', empresaId)
      .neq('estado', 'borrador')          // borradores no aparecen en historial
      .order('numero', { ascending: false });
    if (aplicarFechas) q = q.gte('fecha_desde', fechaDesde).lte('fecha_hasta', fechaHasta);

    // Al buscar por número se ignora el filtro de estado (búsqueda libre).
    if (!buscandoNumero) {
      // 'manual' es transversal (atributo, no estado): filtra por la bandera manual.
      if (filtroEstado === 'manual') q = q.eq('manual', true);
      else if (filtroEstado !== 'todos') q = q.eq('estado', filtroEstado);
    }
    if (busqueda.trim()) q = q.ilike('numero', `%${busqueda.trim()}%`);
    if (!buscandoNumero && busquedaSemana.trim()) q = q.ilike('semana', `%${busquedaSemana.trim()}%`);

    // Query paralela para conteos por estado (sin filtro de estado)
    let qConteos = supabase
      .from('app_boletas')
      .select('estado, manual')
      .eq('empresa_id', empresaId)
      .neq('estado', 'borrador');
    if (aplicarFechas) qConteos = qConteos.gte('fecha_desde', fechaDesde).lte('fecha_hasta', fechaHasta);
    if (busqueda.trim()) qConteos = qConteos.ilike('numero', `%${busqueda.trim()}%`);
    if (!buscandoNumero && busquedaSemana.trim()) qConteos = qConteos.ilike('semana', `%${busquedaSemana.trim()}%`);

    // Conteo VFP (independiente del filtro de estado)
    let qVfpCount = supabase
      .from('app_vfp_cedula')
      .select('nboleta', { count: 'exact', head: true })
      .eq('empresa_id', empresaId);
    if (aplicarFechas) qVfpCount = qVfpCount.gte('faplica', fechaDesde).lte('faplica', fechaHasta);
    if (busqueda.trim()) qVfpCount = qVfpCount.ilike('nboleta', `%${busqueda.trim()}%`);
    if (!buscandoNumero && busquedaSemana.trim()) qVfpCount = qVfpCount.ilike('semana', `%${busquedaSemana.trim()}%`);

    const [{ data, error }, { data: dataConteos }, { count: countVfp }] = await Promise.all([q, qConteos, qVfpCount]);
    if (seq !== cargarSeqRef.current) return;
    setLoading(false);
    if (!error) {
      const nextBoletas = (data ?? []) as AppBoleta[];
      setBoletas(nextBoletas);
      setBoletaSel(prev => {
        if (!prev) return null;
        return nextBoletas.find(b => b.id === prev.id) ?? null;
      });
    }
    if (dataConteos) {
      const mapa: Record<string, number> = { todos: dataConteos.length };
      for (const row of dataConteos as { estado: string; manual: boolean | null }[]) {
        mapa[row.estado] = (mapa[row.estado] ?? 0) + 1;
        if (row.manual) mapa.manual = (mapa.manual ?? 0) + 1;
      }
      mapa.vfp = countVfp ?? 0;
      setConteos(mapa);
    }
  }, [empresaId, fechaDesde, fechaHasta, filtroFechasActivo, filtroEstado, busqueda, busquedaSemana, refreshTrigger]);

  useEffect(() => { cargar(); }, [cargar]);

  // Cargar productos de boleta seleccionada
  useEffect(() => {
    if (!boletaSel) { setProductos([]); return; }
    supabase
      .from('app_boleta_productos')
      .select('*')
      .eq('boleta_id', boletaSel.id)
      .order('es_agua')
      .order('orden')
      .then(({ data }) => setProductos((data ?? []) as AppBoletaProducto[]));
  }, [boletaSel]);

  // Cargar productos del acordeón bajo demanda (con cache)
  useEffect(() => {
    if (expandidoId == null) return;
    setLoadingExpandido(expandidoId);
    // Identificar si es VFP por id negativo (los nuevos son id positivo en BD)
    const esVfp = expandidoId < 0;

    // Estado de movimiento (una sola consulta por boleta expandida, no por fila).
    // Las boletas VFP históricas (id negativo) quedan fuera de este tracking.
    if (!esVfp && movimientosBoleta[expandidoId] === undefined) {
      supabase
        .from('inv_movimientos')
        .select('referencia')
        .eq('empresa_id', empresaId)
        .or(`referencia.like.APP-BOLETA-${expandidoId}-PROD-%,referencia.like.APP-BOLETA-DIRECTA-${expandidoId}-PROD-%`)
        .then(({ data }) => {
          const set = new Set<number>();
          for (const row of data ?? []) {
            const m = String(row.referencia || '').match(/-PROD-(\d+)$/);
            if (m) set.add(Number(m[1]));
          }
          setMovimientosBoleta((prev) => ({ ...prev, [expandidoId]: set }));
        });
    }
    if (esVfp) {
      // Recuperar el nboleta desde la boleta cargada
      const b = boletas.find(x => x.id === expandidoId);
      if (!b) {
        setLoadingExpandido(null);
        return;
      }
      supabase
        .from('app_vfp_cedula_detalle')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('nboleta', b.numero)
        .order('orden')
        .then(({ data }) => {
          // Mapear al shape AppBoletaProducto
          const prods = (data ?? []).map((d: any): AppBoletaProducto => ({
            id: d.id,
            empresa_id: d.empresa_id,
            boleta_id: expandidoId,
            articulo: d.articulo ?? '',
            nombre_producto: d.desc_artic ?? '',
            descripcion: d.descripcio ?? '',
            justificacion: d.justificacion ?? d.catalogo ?? '',
            grupo_quimico: d.grupo_quimico ?? '',
            medida: d.medida ?? '',
            reingreso: d.reingreso ?? '',
            latencia: d.latencia ?? undefined,
            orden: d.orden ?? 0,
            dosis: d.dosis ?? 0,
            volumen: d.volumen ?? 0,
            cantidad_aplicar: d.aplicar ?? 0,
            cantidad_boom: d.boom_qty ?? 0,
            cantidad_fraccion: d.fraccion ?? 0,
            es_agua: false,
          } as any));
          setProductosCache((prev) => ({ ...prev, [expandidoId]: prods }));
          setLoadingExpandido((curr) => (curr === expandidoId ? null : curr));
        });
      return;
    }
    supabase
      .from('app_boleta_productos')
      .select('*')
      .eq('boleta_id', expandidoId)
      .order('es_agua')
      .order('orden')
      .then(({ data }) => {
        setProductosCache((prev) => ({ ...prev, [expandidoId]: (data ?? []) as AppBoletaProducto[] }));
        setLoadingExpandido((curr) => (curr === expandidoId ? null : curr));
      });
  }, [expandidoId, boletas, empresaId]);

  function toggleExpandido(id: number) {
    setExpandidoId((curr) => (curr === id ? null : id));
  }

  // Movimiento de inventario real de un producto de boleta — mismo patrón de
  // referencia usado por el backend (aplicaciones-bodega.js) y por el ERP
  // (contabilizacion.js): APP-BOLETA-{boletaId}-PROD-{boletaProductoId}, o su
  // variante -DIRECTA- cuando la boleta se aplicó sin flujo bodega/boomero.
  async function abrirMovimientoInventario(boletaId: number, prodId: number, nombre: string, boletaNumero: string) {
    setMovModal({ boletaId, prodId, nombre, boletaNumero });
    setMovModalData(null);
    setMovModalLoading(true);
    try {
      const refNormal = `APP-BOLETA-${boletaId}-PROD-${prodId}`;
      const refDirecta = `APP-BOLETA-DIRECTA-${boletaId}-PROD-${prodId}`;
      const { data, error } = await supabase
        .from('inv_movimientos')
        .select('id,tipo,cantidad,costo_unitario,fecha,referencia,origen,notas,bodega_id')
        .eq('empresa_id', empresaId)
        .in('referencia', [refNormal, refDirecta])
        .order('fecha', { ascending: true });
      if (error) throw error;
      setMovModalData(data ?? []);
    } catch {
      setMovModalData([]);
    } finally {
      setMovModalLoading(false);
    }
  }

  // Buscar por número al presionar Enter
  function handleBuscarKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') cargar();
  }

  // Boletas ordenadas (derivado)
  const boletasOrdenadas = sortKey ? sortBoletas(boletas, sortKey, sortDir) : boletas;

  const estadisticaSemana = useMemo(() => {
    const semanaActiva = busquedaSemana.trim();
    const rows = semanaActiva
      ? boletas.filter(b => String(b.semana ?? '').trim() === semanaActiva)
      : boletas;
    const total = rows.length;
    const porEstado = rows.reduce<Record<string, number>>((acc, b) => {
      acc[b.estado] = (acc[b.estado] ?? 0) + 1;
      return acc;
    }, {});
    const plantas = rows.reduce((s, b) => s + Number(b.plantas ?? 0), 0);
    const area = rows.reduce((s, b) => s + Number(b.area_ha ?? 0), 0);
    const agua = rows.reduce((s, b) => s + Number((b as any).agua_total_l ?? b.agua_l ?? 0), 0);
    const boomsTotal = rows.reduce((s, b) => s + Number((b as any).n_booms_completos ?? 0), 0);
    const lotes = new Set(rows.map(b => String(b.lote_num ?? '').trim()).filter(Boolean)).size;
    const bloques = new Set(
      rows.flatMap(b => String(b.bloques ?? '').split(/[,;\s]+/).map(x => x.trim()).filter(Boolean))
    ).size;
    const semanas = [...new Set(rows.map(b => String(b.semana ?? '').trim()).filter(Boolean))].sort();
    return {
      rows,
      total,
      titulo: semanaActiva ? `Semana ${semanaActiva}` : (semanas.length === 1 ? `Semana ${semanas[0]}` : 'Rango visible'),
      porEstado,
      plantas,
      area,
      densidad: area > 0 ? plantas / area : 0,
      agua,
      boomsTotal,
      lotes,
      bloques,
      topLotes: topValores(rows, 'lote_num'),
      topPlanes: topValores(rows, 'etapa_nombre'),
      topCiclos: topValores(rows, 'ciclo_nombre'),
    };
  }, [boletas, busquedaSemana]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  // Navegar con teclado arriba/abajo en la tabla
  function handleTablaKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && boletaSel) {
      e.preventDefault();
      toggleMarcada(boletaSel.id);
      return;
    }
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    if (boletasOrdenadas.length === 0) return;
    const idx = boletaSel ? boletasOrdenadas.findIndex(b => b.id === boletaSel.id) : -1;
    let next = idx;
    if (e.key === 'ArrowDown') next = idx < boletasOrdenadas.length - 1 ? idx + 1 : idx;
    if (e.key === 'ArrowUp')   next = idx > 0 ? idx - 1 : 0;
    if (next === idx && idx !== -1) return;
    const boleta = boletasOrdenadas[next === -1 ? 0 : next];
    setBoletaSel(boleta);
    rowRefs.current[next === -1 ? 0 : next]?.scrollIntoView({ block: 'nearest' });
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="px-5 py-3 shrink-0 flex items-start gap-6 flex-wrap"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>

        <div className="shrink-0">
          <h1 className={pageTitleCls}>Cédulas de Aplicaciones</h1>
          <p className={pageSubtitleCls}>Historial de boletas — trazabilidad y auditoría</p>
        </div>

        {/* Rango fechas + KPIs Totales debajo */}
        <div className="flex flex-col gap-2">
          <div className="flex items-end gap-3">
            <div>
              <label className={labelCls}>Del</label>
              <input type="date" className={inputCls} style={{ width: '9rem' }}
                value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Al</label>
              <input type="date" className={inputCls} style={{ width: '9rem' }}
                value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
            </div>
            <button onClick={() => setFiltroFechasActivo(true)} disabled={loading || !fechaDesde || !fechaHasta}
              className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium mb-0.5"
              title="Aplicar el filtro de fechas"
              style={{ background: filtroFechasActivo ? 'var(--emp-accent)' : 'var(--surface-deep)', border: '1px solid var(--line)', color: filtroFechasActivo ? 'var(--emp-accent-on)' : 'var(--ink-muted)' }}>
              {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Filtrar por fecha
            </button>
            {filtroFechasActivo && (
              <button onClick={() => setFiltroFechasActivo(false)} disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium mb-0.5"
                title="Quitar el filtro de fechas (ver todo)"
                style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                <X size={13} /> Ver todo
              </button>
            )}
            <button onClick={() => setModalEstadistica(true)} disabled={boletas.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold mb-0.5"
              title={busquedaSemana ? `Ver estadistica de la semana ${busquedaSemana}` : 'Ver estadistica del rango visible'}
              style={{
                background: 'var(--tone-info-bg)',
                border: '1px solid var(--tone-info-border)',
                color: boletas.length > 0 ? 'var(--tone-info-text)' : 'var(--ink-faint)',
                opacity: boletas.length > 0 ? 1 : 0.6,
              }}>
              <BarChart3 size={13} />
              Estadistica
            </button>
          </div>

          {/* KPIs estilo dashboard: mini-cards con fondo translúcido */}
          {boletas.length > 0 && (
            <div className="flex items-stretch gap-2 flex-wrap">
              {[
                { lbl: 'Plantas',  val: totalPlantas.toLocaleString('es-CR'),    accent: 'var(--tone-success-text)' },
                { lbl: 'Área',     val: `${fmtN(totalArea)} ha`,                 accent: 'var(--tone-info-text)' },
                { lbl: 'Densidad', val: fmtN(densMedia, 0),                      accent: 'var(--emp-accent-txt)' },
                { lbl: 'Agua',     val: `${fmtN(aguaTotal, 0)} Lt`,              accent: 'var(--tone-info-text)' },
                { lbl: 'Booms',    val: booms.toLocaleString('es-CR'),           accent: 'var(--tone-warn-text)' },
              ].map(({ lbl, val, accent }) => (
                <div key={lbl}
                  className="px-2.5 py-1.5 rounded-md flex flex-col justify-center"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid var(--line-dim)',
                    minWidth: '4.5rem',
                  }}>
                  <p className="text-[9px] uppercase tracking-widest mb-0.5"
                    style={{ color: 'var(--ink-faint)', letterSpacing: '0.08em' }}>{lbl}</p>
                  <p className="font-bold font-mono text-[13px] leading-tight"
                    style={{ color: accent }}>{val}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buscadores Semana / Boleta a la derecha */}
        <div className="ml-auto flex items-end gap-5">
          <div ref={selectorSemanaRef} className="relative">
            <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Semana</p>
            <button
              type="button"
              onClick={() => setSelectorSemanaAbierto(v => !v)}
              className="flex items-center justify-between gap-3 font-bold font-mono text-sm outline-none"
              style={{
                width: '7.5rem',
                color: 'var(--ink)',
                background: 'var(--surface-deep)',
                border: '1px solid var(--line)',
                borderRadius: '4px',
                padding: '4px 7px',
              }}
              aria-haspopup="listbox"
              aria-expanded={selectorSemanaAbierto}>
              <span>{busquedaSemana || 'Todas'}</span>
              <ChevronDown size={13} style={{
                color: 'var(--ink-muted)',
                transform: selectorSemanaAbierto ? 'rotate(180deg)' : undefined,
                transition: 'transform 120ms ease',
              }} />
            </button>
            {selectorSemanaAbierto && (
              <div
                role="listbox"
                className="absolute right-0 mt-1 overflow-y-auto rounded shadow-xl"
                style={{
                  zIndex: 80,
                  width: '10rem',
                  maxHeight: '18rem',
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--line)',
                  color: 'var(--ink)',
                }}>
                {['', ...semanasDisponibles].map(s => {
                  const activa = busquedaSemana === s;
                  return (
                    <button
                      type="button"
                      role="option"
                      aria-selected={activa}
                      key={s || 'todas'}
                      onClick={() => {
                        setBusquedaSemana(s);
                        setSelectorSemanaAbierto(false);
                      }}
                      className="block w-full px-3 py-2 text-left font-mono text-xs transition-colors"
                      style={{
                        color: activa ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                        background: activa ? 'var(--emp-accent-bg)' : 'transparent',
                        borderBottom: '1px solid var(--line-dim)',
                        fontWeight: activa ? 700 : 500,
                      }}>
                      {s || 'Todas las semanas'}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Boleta</p>
            <div className="flex items-center gap-1">
              <input
                className="font-bold font-mono text-sm bg-transparent outline-none"
                style={{
                  width: '7rem',
                  color: 'var(--ink)',
                  borderBottom: '1px solid var(--line)',
                  padding: '0',
                }}
                placeholder={ultimoNumero || '0000004354'}
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                onKeyDown={handleBuscarKey} />
              {busqueda && (
                <button onClick={() => setBusqueda('')}
                  style={{ color: 'var(--ink-faint)' }}>
                  <X size={11} />
                </button>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ── Filtros estado ─────────────────────────────────────────────────── */}
      <div className="px-5 py-2 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: '1px solid var(--line-dim)', background: 'var(--surface-raised)' }}>
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Ver:</span>
        {FILTROS.map(f => {
          const n = conteos[f.key] ?? 0;
          return (
            <label key={f.key} className="flex items-center gap-1.5 cursor-pointer text-xs"
              style={{ color: filtroEstado === f.key ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}>
              <input type="radio" name="filtroEstado" value={f.key}
                checked={filtroEstado === f.key}
                onChange={() => cambiarFiltro(f.key)} />
              {f.label}
              <span className="font-mono" style={{
                fontSize: '10px',
                opacity: n === 0 ? 0.4 : 1,
                color: filtroEstado === f.key ? 'var(--emp-accent-txt)' : 'var(--ink-faint)',
              }}>({n})</span>
            </label>
          );
        })}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
            {boletas.length} boleta{boletas.length !== 1 ? 's' : ''}
            {fechaDesde && fechaHasta && (
              <span style={{ marginLeft: '6px', color: 'var(--ink-muted)' }}>
                · {fmtRangoCorto(fechaDesde, fechaHasta)}
              </span>
            )}
          </span>
        </div>

        {/* Acciones rápidas */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleEnviarEmailAplicacion} disabled={enviandoEmail || !boletaSel || boletaSel.estado !== 'aplicada'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title={!boletaSel ? 'Seleccione una boleta aplicada' : boletaSel.estado !== 'aplicada' ? 'Solo aplica para boletas aplicadas' : 'Enviar correo de aplicacion'}
            style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: boletaSel?.estado === 'aplicada' ? 'var(--tone-success-text)' : 'var(--ink-faint)', opacity: enviandoEmail ? 0.6 : 1 }}>
            {enviandoEmail ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            Enviar email
          </button>
          <button onClick={handleEnviarWspAplicacion} disabled={enviandoWsp || !boletaSel || boletaSel.estado !== 'aplicada'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title={!boletaSel ? 'Seleccione una boleta aplicada' : boletaSel.estado !== 'aplicada' ? 'Solo aplica para boletas aplicadas' : 'Enviar WhatsApp de aplicacion'}
            style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.30)', color: boletaSel?.estado === 'aplicada' ? '#4ade80' : 'var(--ink-faint)', opacity: enviandoWsp ? 0.6 : 1 }}>
            {enviandoWsp ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
            Enviar WhatsApp
          </button>
          <button onClick={() => boletaSel && setEditarReporte(boletaSel)} disabled={!boletaSel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title={!boletaSel ? 'Seleccione una boleta' : 'Editar el reporte de campo del boomero'}
            style={{ background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.30)', color: boletaSel ? '#a5b4fc' : 'var(--ink-faint)' }}>
            <Edit size={12} />
            Reporte del boomero
          </button>
          {permisos.perm_vob && (
            <button onClick={() => { if (boletaSel) { setRecalcMsg(''); setRecalcularCedula(boletaSel); } }}
              disabled={!boletaSel || (boletaSel?.id ?? 0) <= 0 || !!boletaSel?.manual || boletaSel?.estado === 'aplicada' || boletaSel?.estado === 'cancelada'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
              title={
                !boletaSel ? 'Seleccione una cédula'
                : (boletaSel.id <= 0) ? 'El histórico VFP no se recalcula'
                : boletaSel.manual ? 'Las boletas manuales no se recalculan'
                : (boletaSel.estado === 'aplicada' || boletaSel.estado === 'cancelada') ? 'Ya fue aplicada/cancelada; bodega ya movió inventario, no se puede recalcular'
                : 'Recalcular área/plantas desde el catálogo y regresar a bodega'
              }
              style={{ background: 'rgba(234,179,8,0.10)', border: '1px solid rgba(234,179,8,0.30)', color: (boletaSel && boletaSel.id > 0 && !boletaSel.manual && boletaSel.estado !== 'aplicada' && boletaSel.estado !== 'cancelada') ? 'var(--tone-warn-text)' : 'var(--ink-faint)' }}>
              <Calculator size={12} />
              Recalcular
            </button>
          )}
          <button onClick={handleImprimirSeleccionada} disabled={imprimiendo || !boletaSel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: boletaSel ? 'var(--ink-muted)' : 'var(--ink-faint)', opacity: imprimiendo ? 0.6 : 1 }}>
            {imprimiendo ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
            Imprimir seleccionada
          </button>
          <button onClick={handleImprimirMarcadas} disabled={imprimiendo || marcadasImprimibles.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: marcadasImprimibles.length > 0 ? 'var(--ink-muted)' : 'var(--ink-faint)', opacity: imprimiendo ? 0.6 : 1 }}>
            {imprimiendo ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
            {marcadasImprimibles.length > 0 ? `Imprimir ${marcadasImprimibles.length} marcada${marcadasImprimibles.length > 1 ? 's' : ''}` : 'Imprimir marcadas'}
          </button>
          <button onClick={handleControlCedulas} disabled={boletas.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title="Vista previa del control manual de recepción, con opción de imprimir o enviar por WhatsApp"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: boletas.length > 0 ? 'var(--ink-muted)' : 'var(--ink-faint)' }}>
            <FileText size={12} />
            Control cédulas
          </button>
          <button onClick={handleSeguimientoActivoBiologico} disabled={generandoControlAb}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title="Cédulas aplicadas después del último corte firme, con su estado de inventario y valorización"
            style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)', opacity: generandoControlAb ? 0.6 : 1 }}>
            {generandoControlAb ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Seguimiento AB
          </button>
          <button onClick={handleImprimirRE08} disabled={imprimiendo || boletas.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title={
              marcadasImprimibles.length > 0
                ? `Imprimir Control de Reingresos para ${marcadasImprimibles.length} boleta${marcadasImprimibles.length > 1 ? 's' : ''} marcada${marcadasImprimibles.length > 1 ? 's' : ''}`
                : 'Imprimir Control de Reingresos (RE-08) para todas las boletas visibles'
            }
            style={{ background: 'var(--tone-info-bg)', border: '1px solid var(--tone-info-border)', color: 'var(--tone-info-text)', opacity: imprimiendo ? 0.6 : 1 }}>
            {imprimiendo ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Control Reingresos{marcadasImprimibles.length > 0 ? ` (${marcadasImprimibles.length})` : ' (todos)'}
          </button>
          <button onClick={abrirReporteProductos} disabled={boletas.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title="Consolidado de productos requeridos por semana (bodega / compras). Excluye manuales y canceladas."
            style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: boletas.length > 0 ? 'var(--tone-warn-text)' : 'var(--ink-faint)', opacity: boletas.length > 0 ? 1 : 0.6 }}>
            <Package size={12} />
            Productos por semana
          </button>
          <button onClick={handleControlInventario} disabled={generandoInventario || boletas.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            title="Control de Inventario: requerido vs saldo, con faltantes (cédulas visibles, excluye manuales/canceladas)"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: boletas.length > 0 ? 'var(--ink-muted)' : 'var(--ink-faint)', opacity: generandoInventario ? 0.6 : 1 }}>
            {generandoInventario ? <Loader2 size={12} className="animate-spin" /> : <Package size={12} />}
            Control Inventario
          </button>
        </div>
        {controlAbMsg && (
          <span className="text-xs" style={{
            color: controlAbMsg.toLowerCase().includes('generado')
              ? 'var(--tone-success-text)'
              : 'var(--tone-warn-text)',
          }}>
            {controlAbMsg}
          </span>
        )}
        {emailMsg && (
          <span className="text-xs" style={{ color: emailMsg.toLowerCase().includes('enviado') ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
            {emailMsg}
          </span>
        )}
        {wspMsg && (
          <span className="text-xs" style={{ color: wspMsg.toLowerCase().includes('enviado') ? '#4ade80' : 'var(--tone-warn-text)' }}>
            {wspMsg}
          </span>
        )}
      </div>

      {/* ── Body 3 paneles ─────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 overflow-auto">

        {/* Panel 1: Tabla boletas — altura natural según filas */}
        <div ref={tablaRef} className="outline-none shrink-0"
          tabIndex={0} onKeyDown={handleTablaKey}
          style={{ borderBottom: '2px solid var(--line)' }}>
          {loading && boletas.length > 0 && (
            <div
              className="flex items-center justify-center gap-2 py-1 text-[11px]"
              style={{
                background: 'var(--surface-overlay)',
                borderBottom: '1px solid var(--line-dim)',
                color: 'var(--ink-muted)',
              }}
            >
              <Loader2 size={12} className="animate-spin" />
              Actualizando vista
            </div>
          )}
          {loading && boletas.length === 0 ? (
            <div className="flex items-center justify-center py-10 gap-2" style={{ color: 'var(--ink-faint)' }}>
              <Loader2 size={18} className="animate-spin" /><span className="text-xs">Cargando…</span>
            </div>
          ) : boletas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 opacity-40">
              <FileText size={32} style={{ color: 'var(--ink-faint)' }} />
              <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin boletas en el rango seleccionado</p>
            </div>
          ) : (
            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-raised)' }}>
                <tr style={{ borderBottom: '1px solid var(--line)' }}>
                  {COLS.map((col, ci) => {
                    const sortable = col.key !== null;
                    const active   = sortable && col.key === sortKey;
                    // Columna "Imprimir": checkbox maestro para marcar/desmarcar todas
                    // las boletas visibles (filtradas). Permite reimprimir toda la semana.
                    if (col.label === 'Imprimir') {
                      const idsVisibles = boletasOrdenadas.filter(b => !b.manual).map(b => b.id);
                      const todasMarcadas = idsVisibles.length > 0 && idsVisibles.every(id => marcadas.has(id));
                      const algunaMarcada = idsVisibles.some(id => marcadas.has(id));
                      return (
                        <th key={ci} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap select-none"
                          style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>
                          <span className="inline-flex flex-col items-center gap-0.5">
                            <input
                              type="checkbox"
                              checked={todasMarcadas}
                              ref={el => { if (el) el.indeterminate = algunaMarcada && !todasMarcadas; }}
                              onChange={() => toggleTodas(idsVisibles)}
                              title={todasMarcadas ? 'Desmarcar todas' : 'Marcar todas'}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '8px' }}>Imprimir</span>
                          </span>
                        </th>
                      );
                    }
                    return (
                      <th key={ci}
                        onClick={() => sortable && toggleSort(col.key as SortKey)}
                        className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap select-none"
                        style={{
                          textAlign: col.align ?? 'left',
                          color: active ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                          cursor: sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}>
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          <SortIcon col={col.key} sortKey={sortKey} sortDir={sortDir} />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {boletasOrdenadas.map((b, i) => {
                  const sel = boletaSel?.id === b.id;
                  const est = ESTADO_CFG[b.estado] ?? ESTADO_CFG.pendiente;
                  const expandido = expandidoId === b.id;
                  const productosExp = productosCache[b.id] ?? [];
                  const cargandoExp = loadingExpandido === b.id;
                  return (
                    <React.Fragment key={b.id}>
                    <tr
                      ref={el => { rowRefs.current[i] = el; }}
                      onClick={() => { setBoletaSel(sel ? null : b); tablaRef.current?.focus(); }}
                      onDoubleClick={() => toggleMarcada(b.id)}
                      className="cursor-pointer transition-colors"
                      style={{
                        borderBottom: expandido ? 'none' : '1px solid var(--line-dim)',
                        background: sel
                          ? 'var(--emp-accent-bg)'
                          : i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                      }}>
                      {/* Chevron acordeón */}
                      <td className="px-2 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleExpandido(b.id); }}
                          className="inline-flex items-center justify-center rounded transition-colors hover:bg-white/5"
                          style={{ width: 22, height: 22, color: expandido ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}
                          title={expandido ? 'Cerrar detalle' : 'Ver detalle de productos'}
                        >
                          {expandido ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                      {/* Activo */}
                      <td className="px-3 py-1.5">
                        <span className="text-[10px] font-medium" style={{ color: b.estado !== 'cancelada' ? 'var(--tone-success-text)' : 'var(--ink-faint)' }}>
                          {b.estado !== 'cancelada' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      {/* Número */}
                      <td className="px-3 py-1.5 font-mono font-bold text-[11px]" style={{ color: 'var(--emp-accent-txt)' }}>
                        <span className="flex items-center gap-1">
                          {b.numero}
                          {/* Badge origen: VFP (id<0) o ERP (id>0). Permite distinguir histórico de transaccional. */}
                          {b.id < 0 ? (
                            <span className="px-1 py-0 rounded text-[9px] font-bold"
                              style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}>
                              VFP
                            </span>
                          ) : (
                            <span className="px-1 py-0 rounded text-[9px] font-bold"
                              style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}>
                              ERP
                            </span>
                          )}
                          {b.manual && (
                            <span className="px-1 py-0 rounded text-[9px] font-bold"
                              style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>M</span>
                          )}
                        </span>
                      </td>
                      {/* Semana */}
                      <td className="px-3 py-1.5 font-mono font-bold text-[11px]" style={{ color: 'var(--tone-info-text)' }}>
                        {b.semana || '—'}
                      </td>
                      {/* Fecha de creación (solo desde la semana 26-26) */}
                      <td className="px-3 py-1.5 font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                        {fmtCreada(b.created_at, b.semana)}
                      </td>
                      {/* Estado */}
                      <td className="px-3 py-1.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                          style={{ background: est.bg, border: `1px solid ${est.border}`, color: est.text }}>
                          {est.label}
                        </span>
                      </td>
                      {/* Imprimir checkbox */}
                      <td className="px-3 py-1.5 text-center">
                        <input type="checkbox"
                          checked={marcadas.has(b.id)}
                          onChange={() => toggleMarcada(b.id)}
                          onClick={e => e.stopPropagation()} />
                      </td>
                      {/* Fechas */}
                      <td className="px-3 py-1.5 font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>{fmtF(b.fecha_aplicacion)}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px] font-semibold" style={{ color: 'var(--ink)' }}>{fmtF(b.fecha_plan)}</td>
                      {/* Boom / Agua */}
                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{b.boom_capacidad_l?.toLocaleString('es-CR') ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{b.agua_l?.toLocaleString('es-CR') ?? '—'}</td>
                      {/* Lote */}
                      <td className="px-3 py-1.5 font-mono font-bold" style={{ color: 'var(--ink)' }}>{b.lote_num ?? '—'}</td>
                      {/* Plantas / Área */}
                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink)' }}>{b.plantas?.toLocaleString('es-CR') ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmtN(b.area_ha)}</td>
                      {/* Plan / Ciclo / Días */}
                      <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-muted)', maxWidth: '8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.etapa_nombre}
                      </td>
                      <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>{b.ciclo_nombre}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--tone-warn-text)' }}>{b.dias ?? '—'}</td>
                      {/* Siembra / Bloques */}
                      <td className="px-3 py-1.5 font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{fmtF(b.fecha_siembra)}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px]"
                        style={{ color: 'var(--ink-faint)', maxWidth: '10rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.bloques}
                      </td>
                    </tr>
                    {expandido && (
                      <tr style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-deep)' }}>
                        <td colSpan={COLS.length} style={{ padding: 0 }}>
                          <div style={{ borderLeft: '3px solid var(--emp-accent)' }}>
                            <div
                              className="text-[11px] font-bold uppercase tracking-widest"
                              style={{
                                color: 'var(--tone-info-text)',
                                background: 'var(--tone-info-bg)',
                                borderBottom: '1px solid var(--tone-info-border)',
                                padding: '8px 16px 8px 35px',
                              }}
                            >
                              Detalle de productos · #{b.numero} · Lote {b.lote_num} · {b.etapa_nombre} / {b.ciclo_nombre}
                            </div>
                            <div style={{ padding: '8px 16px 12px 35px' }}>
                            {cargandoExp ? (
                              <div className="flex items-center gap-2 py-3" style={{ color: 'var(--ink-faint)' }}>
                                <Loader2 size={14} className="animate-spin" /><span className="text-xs">Cargando productos…</span>
                              </div>
                            ) : productosExp.length === 0 ? (
                              <div className="py-3 text-xs opacity-50" style={{ color: 'var(--ink-faint)' }}>
                                Sin productos registrados
                              </div>
                            ) : (
                              <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--line)' }}>
                                    {['Artículo', 'Nombre Producto', 'Descripción', 'Justificación', 'Dosis', 'Boom', 'Fracción', 'Aplicar', 'Latencia', 'Reingreso'].map(h => (
                                      <th key={h} className="px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                                        style={{ color: 'var(--ink-muted)' }}>{h}</th>
                                    ))}
                                    <th className="px-3 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                                      style={{ color: 'var(--ink-muted)' }}>Inv.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {productosExp.map((p, j) => (
                                    <tr key={p.id}
                                      style={{
                                        borderBottom: '1px solid var(--line-dim)',
                                        background: p.es_agua
                                          ? 'var(--tone-info-bg)'
                                          : j % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                                      }}>
                                      <td className="px-3 py-1.5 font-mono font-bold text-[11px]" style={{ color: 'var(--emp-accent-txt)' }}>{p.articulo}</td>
                                      <td className="px-3 py-1.5 font-medium" style={{ color: 'var(--ink)', whiteSpace: 'nowrap' }}>{p.nombre_producto}</td>
                                      <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{p.descripcion ?? '—'}</td>
                                      <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-faint)' }}>{(p as any).justificacion || '—'}</td>
                                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink)' }}>{fmtN(p.dosis, 3)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--ink)' }}>{fmtN(p.cantidad_boom, 5)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmtN(p.cantidad_fraccion, 5)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtN(p.cantidad_aplicar, 3)}</td>
                                      <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-faint)' }}>{p.latencia ?? '—'}</td>
                                      <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-faint)' }}>{p.reingreso ?? '—'}</td>
                                      <td className="px-3 py-1.5 text-center">
                                        {!p.es_agua && b.id > 0 && (() => {
                                          const movSet = movimientosBoleta[b.id];
                                          const cargando = movSet === undefined;
                                          const tieneMov = !cargando && movSet.has(p.id);
                                          return (
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); abrirMovimientoInventario(b.id, p.id, p.nombre_producto, b.numero); }}
                                              title={cargando ? 'Cargando estado de inventario…' : tieneMov ? 'Sí descontó inventario — clic para ver el detalle' : 'NO descontó inventario — clic para revisar'}
                                              className="inline-flex items-center justify-center w-5 h-5 rounded-full hover-surface"
                                            >
                                              <span
                                                className="block rounded-full"
                                                style={{
                                                  width: 8, height: 8,
                                                  background: cargando ? 'var(--ink-faint)' : tieneMov ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
                                                  opacity: cargando ? 0.4 : 1,
                                                }}
                                              />
                                            </button>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Modal estadistica semanal */}
      {modalEstadistica && createPortal((
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,0.62)' }}
          onClick={() => setModalEstadistica(false)}
        >
          <div
            className="w-full max-w-5xl rounded-lg overflow-hidden shadow-2xl"
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--line)',
              color: 'var(--ink)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="flex items-start justify-between gap-4 px-5 py-4"
              style={{ borderBottom: '1px solid var(--line)' }}
            >
              <div>
                <div className="flex items-center gap-2">
                  <BarChart3 size={18} style={{ color: 'var(--tone-info-text)' }} />
                  <h2 className="text-lg font-bold">Estadistica de aplicaciones</h2>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                  {estadisticaSemana.titulo} · {fmtRangoCorto(fechaDesde, fechaHasta)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalEstadistica(false)}
                className="inline-flex items-center justify-center rounded-md"
                style={{
                  width: 36,
                  height: 36,
                  background: 'var(--surface-deep)',
                  border: '1px solid var(--line)',
                  color: 'var(--ink-muted)',
                }}
                title="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-5 max-h-[78vh] overflow-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Boletas', value: estadisticaSemana.total.toLocaleString('es-CR'), tone: 'info' },
                  { label: 'Aplicadas', value: `${estadisticaSemana.porEstado.aplicada ?? 0} (${pct(estadisticaSemana.porEstado.aplicada ?? 0, estadisticaSemana.total)})`, tone: 'success' },
                  { label: 'Pendientes', value: `${estadisticaSemana.porEstado.pendiente ?? 0} (${pct(estadisticaSemana.porEstado.pendiente ?? 0, estadisticaSemana.total)})`, tone: 'warn' },
                  { label: 'Canceladas', value: (estadisticaSemana.porEstado.cancelada ?? 0).toLocaleString('es-CR'), tone: 'plain' },
                  { label: 'Plantas', value: estadisticaSemana.plantas.toLocaleString('es-CR'), tone: 'success' },
                  { label: 'Area', value: `${fmtN(estadisticaSemana.area)} ha`, tone: 'info' },
                  { label: 'Densidad', value: fmtN(estadisticaSemana.densidad, 0), tone: 'plain' },
                  { label: 'Agua', value: `${fmtN(estadisticaSemana.agua, 0)} Lt`, tone: 'info' },
                  { label: 'Booms', value: estadisticaSemana.boomsTotal.toLocaleString('es-CR'), tone: 'warn' },
                  { label: 'Lotes', value: estadisticaSemana.lotes.toLocaleString('es-CR'), tone: 'plain' },
                  { label: 'Bloques', value: estadisticaSemana.bloques.toLocaleString('es-CR'), tone: 'plain' },
                  { label: 'VFP/ERP', value: `${estadisticaSemana.rows.filter(b => b.id < 0).length}/${estadisticaSemana.rows.filter(b => b.id > 0).length}`, tone: 'info' },
                ].map(k => {
                  const toneStyle =
                    k.tone === 'success' ? { bg: 'var(--tone-success-bg)', border: 'var(--tone-success-border)', text: 'var(--tone-success-text)' } :
                    k.tone === 'warn' ? { bg: 'var(--tone-warn-bg)', border: 'var(--tone-warn-border)', text: 'var(--tone-warn-text)' } :
                    k.tone === 'info' ? { bg: 'var(--tone-info-bg)', border: 'var(--tone-info-border)', text: 'var(--tone-info-text)' } :
                    { bg: 'var(--surface-deep)', border: 'var(--line)', text: 'var(--ink)' };
                  return (
                    <div
                      key={k.label}
                      className="rounded-md px-4 py-3"
                      style={{ background: toneStyle.bg, border: `1px solid ${toneStyle.border}` }}
                    >
                      <p className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--ink-muted)' }}>{k.label}</p>
                      <p className="mt-2 text-xl font-bold font-mono" style={{ color: toneStyle.text }}>{k.value}</p>
                    </div>
                  );
                })}
              </div>

              {estadisticaSemana.total === 0 ? (
                <div className="py-10 text-center rounded-md" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                  No hay datos para la semana seleccionada.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {[
                    { title: 'Lotes principales', rows: estadisticaSemana.topLotes },
                    { title: 'Planes', rows: estadisticaSemana.topPlanes },
                    { title: 'Ciclos', rows: estadisticaSemana.topCiclos },
                  ].map(section => (
                    <div key={section.title} className="rounded-md overflow-hidden" style={{ border: '1px solid var(--line)', background: 'var(--surface-deep)' }}>
                      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--line)' }}>
                        <h3 className="text-sm font-bold">{section.title}</h3>
                      </div>
                      <div>
                        {section.rows.map(row => (
                          <div
                            key={row.label}
                            className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 text-xs"
                            style={{ borderBottom: '1px solid var(--line-dim)' }}
                          >
                            <div>
                              <p className="font-bold" style={{ color: 'var(--ink)' }}>{row.label}</p>
                              <p className="mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                                {fmtN(row.area)} ha · {row.plantas.toLocaleString('es-CR')} plantas
                              </p>
                            </div>
                            <div className="font-mono font-bold" style={{ color: 'var(--tone-info-text)' }}>
                              {row.total}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Modal editar reporte de campo */}
      {editarReporte && (
        <ModalReporteCampo
          boleta={editarReporte}
          empresaId={empresaId}
          usuario={usuarioActual}
          booms={boomsList}
          onClose={() => setEditarReporte(null)}
          onGuardado={() => { cargar(); }}
        />
      )}

      {/* Modal reporte "Productos requeridos por semana" */}
      {modalProductos && createPortal((
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,0.62)' }}
          onClick={() => setModalProductos(false)}
        >
          <div
            className="w-full max-w-4xl rounded-lg overflow-hidden shadow-2xl flex flex-col"
            style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink)', maxHeight: '90vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 shrink-0"
              style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-deep)' }}>
              <div className="flex items-center gap-2">
                <Package size={18} style={{ color: 'var(--tone-warn-text)' }} />
                <div>
                  <h2 className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    Productos requeridos {consolidado ? `· ${consolidado.titulo}` : ''}
                  </h2>
                  <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                    Consolidado por producto · excluye manuales y canceladas
                  </p>
                </div>
              </div>
              <button onClick={() => setModalProductos(false)} style={{ color: 'var(--ink-faint)' }}>
                <X size={18} />
              </button>
            </div>

            {/* Toolbar acciones */}
            <div className="flex items-center gap-2 px-5 py-2.5 shrink-0 flex-wrap"
              style={{ borderBottom: '1px solid var(--line-dim)' }}>
              <button onClick={handleImprimirProductos} disabled={!consolidado || consolidado.productos.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
                style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: consolidado?.productos.length ? 'var(--ink-muted)' : 'var(--ink-faint)' }}>
                <Printer size={12} /> Imprimir / PDF
              </button>
              <button onClick={() => enviarReporteProductos('email')} disabled={enviandoProdEmail || !consolidado || consolidado.productos.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
                style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: consolidado?.productos.length ? 'var(--tone-success-text)' : 'var(--ink-faint)', opacity: enviandoProdEmail ? 0.6 : 1 }}>
                {enviandoProdEmail ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Enviar email
              </button>
              <button onClick={() => enviarReporteProductos('whatsapp')} disabled={enviandoProdWsp || !consolidado || consolidado.productos.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
                style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.30)', color: consolidado?.productos.length ? '#4ade80' : 'var(--ink-faint)', opacity: enviandoProdWsp ? 0.6 : 1 }}>
                {enviandoProdWsp ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />} Enviar WhatsApp
              </button>
              <button onClick={() => consolidado && exportarProductosSemanaCSV(consolidado)} disabled={!consolidado || consolidado.productos.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
                title="Exportar a Excel (CSV) con el desglose producto × boleta"
                style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: consolidado?.productos.length ? 'var(--tone-success-text)' : 'var(--ink-faint)' }}>
                <FileSpreadsheet size={12} /> Excel
              </button>
              {prodMsg && (
                <span className="text-xs ml-1" style={{ color: prodMsg.toLowerCase().includes('enviad') ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                  {prodMsg}
                </span>
              )}
            </div>

            {/* KPIs */}
            {consolidado && (
              <div className="flex items-stretch gap-2 px-5 py-2.5 shrink-0 flex-wrap"
                style={{ borderBottom: '1px solid var(--line-dim)' }}>
                {[
                  { lbl: 'Boletas',   val: consolidado.boletasIncluidas.toLocaleString('es-CR') },
                  { lbl: 'Productos', val: consolidado.productos.length.toLocaleString('es-CR') },
                  { lbl: 'Área',      val: `${fmtN(consolidado.areaTotal)} ha` },
                  { lbl: 'Plantas',   val: consolidado.plantasTotal.toLocaleString('es-CR') },
                ].map(({ lbl, val }) => (
                  <div key={lbl} className="px-2.5 py-1.5 rounded-md"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--line-dim)', minWidth: '5rem' }}>
                    <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>{lbl}</p>
                    <p className="font-bold font-mono text-[13px]" style={{ color: 'var(--tone-warn-text)' }}>{val}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Aviso: reporte sin filtrar por semana ni fechas → suma todo el historial */}
            {consolidado && consolidado.productos.length > 0 && !busquedaSemana.trim() && !filtroFechasActivo && (
              <div className="px-5 py-2 text-[11px] shrink-0"
                style={{ background: 'var(--tone-warn-bg)', borderBottom: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)' }}>
                ⚠️ Este consolidado incluye <strong>todo el historial visible</strong> ({consolidado.boletasIncluidas} boletas). Para un reporte semanal, escriba una <strong>Semana</strong> o active el filtro de fechas y vuelva a abrirlo.
              </div>
            )}

            {/* Tabla */}
            <div className="flex-1 overflow-auto">
              {cargandoConsolidado ? (
                <div className="flex items-center justify-center gap-2 py-12" style={{ color: 'var(--ink-faint)' }}>
                  <Loader2 size={18} className="animate-spin" /><span className="text-xs">Consolidando productos…</span>
                </div>
              ) : !consolidado || consolidado.productos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 opacity-40">
                  <Package size={32} style={{ color: 'var(--ink-faint)' }} />
                  <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Sin productos requeridos en el rango.</p>
                </div>
              ) : (
                <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-raised)' }}>
                    <tr style={{ borderBottom: '1px solid var(--line)' }}>
                      {['', '#', 'Artículo', 'Nombre Comercial', 'Ingrediente Activo', 'Unidad', 'Cantidad', 'Boletas'].map((h, i) => (
                        <th key={i} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider"
                          style={{ textAlign: i >= 5 ? (i === 6 ? 'right' : 'center') : 'left', color: 'var(--ink-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {consolidado.productos.map((p, i) => {
                      const key = `${p.articulo}||${p.medida}`;
                      const abierto = prodExpandido === key;
                      return (
                        <React.Fragment key={key}>
                          <tr
                            onClick={() => setProdExpandido(abierto ? null : key)}
                            className="cursor-pointer"
                            title="Ver las boletas que suman este producto"
                            style={{ borderBottom: abierto ? 'none' : '1px solid var(--line-dim)', background: abierto ? 'var(--emp-accent-bg)' : i % 2 === 0 ? 'transparent' : 'var(--surface-deep)' }}>
                            <td className="px-2 py-1.5 text-center" style={{ color: abierto ? 'var(--emp-accent-txt)' : 'var(--ink-faint)' }}>
                              {abierto ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            </td>
                            <td className="px-3 py-1.5 text-center" style={{ color: 'var(--ink-faint)' }}>{i + 1}</td>
                            <td className="px-3 py-1.5 font-mono font-bold text-[11px]" style={{ color: 'var(--emp-accent-txt)' }}>{p.articulo}</td>
                            <td className="px-3 py-1.5 font-medium" style={{ color: 'var(--ink)' }}>{p.nombre_producto}</td>
                            <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-faint)' }}>{p.ingrediente || '—'}</td>
                            <td className="px-3 py-1.5 text-center text-[11px]" style={{ color: 'var(--ink-muted)' }}>{p.medida || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtN(p.cantidad, 2)}</td>
                            <td className="px-3 py-1.5 text-center font-mono" style={{ color: 'var(--ink-muted)' }}>{p.boletas}</td>
                          </tr>
                          {abierto && (
                            <tr style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-deep)' }}>
                              <td colSpan={8} style={{ padding: 0 }}>
                                <div style={{ borderLeft: '3px solid var(--emp-accent)', padding: '6px 16px 10px 40px' }}>
                                  <table className="w-full text-[11px]" style={{ borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '1px solid var(--line-dim)' }}>
                                        {['Boleta', 'Semana', 'Lote', 'Área (ha)', 'Aporta'].map((h, j) => (
                                          <th key={h} className="px-3 py-1 text-[9px] font-bold uppercase tracking-wider"
                                            style={{ textAlign: j >= 3 ? 'right' : 'left', color: 'var(--ink-faint)' }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {p.aportes.map((a, k) => (
                                        <tr key={`${a.numero}-${k}`} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                                          <td className="px-3 py-1 font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{a.numero}</td>
                                          <td className="px-3 py-1 font-mono" style={{ color: 'var(--tone-info-text)' }}>{a.semana || '—'}</td>
                                          <td className="px-3 py-1 font-mono font-bold" style={{ color: 'var(--ink)' }}>{a.lote_num || '—'}</td>
                                          <td className="px-3 py-1 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmtN(a.area_ha, 2)}</td>
                                          <td className="px-3 py-1 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtN(a.cantidad, 2)}</td>
                                        </tr>
                                      ))}
                                      <tr style={{ borderTop: '1px solid var(--line)' }}>
                                        <td colSpan={4} className="px-3 py-1 text-right font-bold" style={{ color: 'var(--ink-muted)' }}>Total {p.medida}</td>
                                        <td className="px-3 py-1 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtN(p.cantidad, 2)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Modal confirmar recálculo de cédula */}
      {recalcularCedula && createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,0.62)' }}
          onClick={() => !recalculando && setRecalcularCedula(null)}>
          <div className="w-full max-w-md rounded-lg overflow-hidden shadow-2xl"
            style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink)' }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-5 py-3"
              style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-deep)' }}>
              <Calculator size={18} style={{ color: 'var(--tone-warn-text)' }} />
              <h2 className="text-sm font-bold">Recalcular cédula #{recalcularCedula.numero}</h2>
            </div>
            <div className="px-5 py-4 text-xs" style={{ color: 'var(--ink-muted)', lineHeight: 1.6 }}>
              <p>
                Se re-leerá el <strong>área y las plantas</strong> de los bloques de esta cédula
                (lote {recalcularCedula.lote_num}) desde el catálogo actual, y se recalcularán todas
                las cantidades (dosis × área), el agua y los booms.
              </p>
              <p className="mt-2">
                La cédula volverá al estado <strong style={{ color: 'var(--tone-warn-text)' }}>Pendiente</strong> para
                que bodega la despache de nuevo. No se anula ni cambia el número.
              </p>
              <div className="mt-3 p-2 rounded" style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}>
                <div className="flex justify-between"><span>Área actual (congelada):</span><span className="font-mono font-bold">{fmtN(recalcularCedula.area_ha)} ha</span></div>
                <div className="flex justify-between"><span>Plantas actuales:</span><span className="font-mono font-bold">{(recalcularCedula.plantas ?? 0).toLocaleString('es-CR')}</span></div>
              </div>
              {recalcMsg && (
                <p className="mt-3 font-medium" style={{ color: recalcMsg.startsWith('Recalculada') ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                  {recalcMsg}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3"
              style={{ borderTop: '1px solid var(--line)' }}>
              <button onClick={() => setRecalcularCedula(null)} disabled={recalculando}
                className="px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                Cancelar
              </button>
              <button onClick={confirmarRecalculo} disabled={recalculando || recalcMsg.startsWith('Recalculada')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)', opacity: recalculando ? 0.6 : 1 }}>
                {recalculando ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
                Recalcular y regresar a bodega
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Modal: movimiento de inventario de un producto de boleta */}
      {movModal && createPortal((
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,0.62)' }}
          onClick={() => setMovModal(null)}
        >
          <div
            className="w-full max-w-2xl rounded-lg overflow-hidden shadow-2xl"
            style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-5 py-4" style={{ borderBottom: '1px solid var(--line)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <Package size={18} style={{ color: 'var(--tone-info-text)' }} />
                  <h2 className="text-base font-bold">Movimiento de inventario</h2>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                  {movModal.nombre} · Boleta #{movModal.boletaNumero}
                </p>
              </div>
              <button type="button" onClick={() => setMovModal(null)} className="p-1 rounded hover-surface" style={{ color: 'var(--ink-faint)' }}>
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4">
              {movModalLoading ? (
                <div className="flex items-center gap-2 py-3" style={{ color: 'var(--ink-faint)' }}>
                  <Loader2 size={14} className="animate-spin" /><span className="text-xs">Buscando movimiento…</span>
                </div>
              ) : !movModalData || movModalData.length === 0 ? (
                <div className="py-3 text-xs" style={{ color: 'var(--tone-warn-text)' }}>
                  ⚠️ Este producto NO tiene ningún movimiento de inventario registrado — el descuento de
                  stock nunca se aplicó para esta línea.
                </div>
              ) : (
                <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--line)' }}>
                      {['Fecha', 'Tipo', 'Cantidad', 'Costo unit.', 'Origen', 'Referencia'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                          style={{ color: 'var(--ink-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {movModalData.map((m) => (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                        <td className="px-2 py-1.5 font-mono">{m.fecha}</td>
                        <td className="px-2 py-1.5 font-medium"
                          style={{ color: m.tipo === 'salida' ? 'var(--tone-warn-text)' : 'var(--tone-success-text)' }}>
                          {m.tipo}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtN(m.cantidad, 3)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtN(m.costo_unitario, 2)}</td>
                        <td className="px-2 py-1.5">{m.origen ?? '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: 'var(--ink-faint)' }}>{m.referencia}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ), document.body)}

      {previewControlCedulas && (
        <ControlCedulasPreview
          boletas={previewControlCedulas.boletas}
          filtros={previewControlCedulas.filtros}
          empresaNombre={empresaNombre}
          empresaCedula={empresaCedula}
          empresaLogo={empresaLogo}
          empresaId={empresaId}
          onClose={() => setPreviewControlCedulas(null)}
        />
      )}
    </div>
  );
}
